import { MortgageAnnotations, Ticker } from '@cian/adfox-templates';
import { useBooleanState } from '@cian/react-utils';
import { Button, IconButton, Image, Label, UIHeading4, UIText2 } from '@cian/ui-kit';
import { IconActionClose16 } from '@cian/ui-kit-design-tokens/icons';

import { clsx } from 'clsx';
import React, { type FC, useCallback } from 'react';

import { type TgbNativedescGeneralProps } from '../TgbNativedescGeneral';
import { Transport } from '../Transport';

import styles from './TgbNativedescGeneralNarrow.css';

type TgbNativedescGeneralNarrowProps = TgbNativedescGeneralProps;

export const TgbNativedescGeneralNarrow: FC<TgbNativedescGeneralNarrowProps> = props => {
  const {
    title,
    address,
    description,
    annotation,
    image,
    transports,
    url,
    button,
    label,
    mortgageTermsWarning,
    mortgageWarning,
    sizes,
  } = props;

  const { state: isClosed, setTrue: setClosed } = useBooleanState();

  const handleClose = useCallback<(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void>(
    event => {
      event.preventDefault();

      setClosed();
    },
    [setClosed],
  );

  if (isClosed) {
    return <div className={styles['empty']} data-testid="empty_native_adfox_block" />;
  }

  return (
    <a className={styles['container']} href={url} rel="noopener noreferrer" style={sizes} target="_blank">
      <div className={styles['card']}>
        <div className={styles['gallery']}>
          <Image objectFit="cover" src={image} />
          {!!label && (
            <div className={styles['gallery-label']}>
              <Label background="overlay-default" color="text-inverted-default">
                {label}
              </Label>
            </div>
          )}
          <div className={styles['disclaimer']}>
            <Ticker withBackground text={annotation} />
          </div>
        </div>
        <div className={styles['content']}>
          <div className={styles['content-wrapper']}>
            <div className={styles['content-column']}>
              <div className={clsx(styles['content-columnt'], styles['gap-4'])}>
                <UIHeading4 as="h2" color="accent-main-primary">
                  {title}
                </UIHeading4>
                <UIHeading4 as="h3">{description}</UIHeading4>
              </div>
              <div className={clsx(styles['content-column'], styles['.gap-4'])}>
                {!!transports.length && (
                  <div className={styles['transports-container']}>
                    <Transport
                      iconColor={transports[0].iconColor}
                      iconType={transports[0].iconType}
                      key={transports[0].id}
                      name={transports[0].name}
                      remoteness={transports[0].remoteness}
                    />
                  </div>
                )}

                <UIText2 color="gray60_100" data-testid="address">
                  {address}
                </UIText2>
              </div>
            </div>
            <div className={styles['content-footer']}>
              <Button fullWidth data-testid="more-info-button" size="M">
                {button || 'Узнать подробности'}
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div className={styles['close-block']} data-testid="close-block">
        <div>
          <IconButton data-testid="close-button" icon={<IconActionClose16 />} onClick={handleClose} />
        </div>
      </div>

      <div className={styles['mortgage-annotations']}>
        <MortgageAnnotations
          height={sizes.height}
          mortgageTermsWarning={mortgageTermsWarning}
          mortgageWarning={mortgageWarning}
        />
      </div>
    </a>
  );
};
